import React, { useRef, useState, useEffect, useCallback } from "react";
import Webcam from "react-webcam";
import FlipCameraIosIcon from "@mui/icons-material/FlipCameraIos";
import CircleIcon from "@mui/icons-material/Circle";
import CloseIcon from "@mui/icons-material/Close";
import LoginIcon from "@mui/icons-material/Login";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Button,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import googleDriveAxios from "../../common/googleDriveAxios";
import { toast } from "react-toastify";

const FACING_MODE_USER = "user";
const FACING_MODE_ENVIRONMENT = "environment";

const CameraCapture: React.FC<{
  chassisDCNumber: string;
  checkPointName: string;
  deptName: string;
  checkCheckpointRecord: () => void;
}> = ({ chassisDCNumber, checkPointName, deptName, checkCheckpointRecord }) => {
  const webcamRef = useRef<Webcam>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [facingMode, setFacingMode] = useState(FACING_MODE_ENVIRONMENT);
  const [cameraPermission, setCameraPermission] = useState<boolean | null>(
    null
  );
  const [folderName, setFolderName] = useState<string>("");
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setIsAuthenticated] = useState<boolean | null>(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState<boolean>(false);

  const videoConstraints: MediaTrackConstraints = {
    facingMode: facingMode,
  };

  const createFolders = useCallback(async () => {
    setLoading(true);
    if (deptName && chassisDCNumber) {
      try {
        const queryParams = new URLSearchParams({
          chassis_no: chassisDCNumber,
          dept_name: deptName,
          checklist_point: checkPointName,
        }).toString();

        await googleDriveAxios.post(`/create-folder?${queryParams}`);
        setLoading(false);
      } catch (error: any) {
        setLoading(false);
        if (error.response) {
          if (error.response.status === 401) {
            setIsAuthenticated(false);
            setShowLoginPrompt(true);
          } else if (error.response.status === 500) {
            toast.error("Server error: Failed to create folder.");
          } else if (error.response.status === 422) {
            toast.error("Validation error. Check the form data.");
          } else {
            toast.error(
              error.response.data?.detail || "Unexpected server error."
            );
          }
        } else if (error.request) {
          toast.error("Server not responding. Please try again later.");
        } else {
          toast.error("Something went wrong.");
        }
      }
    }
  }, [deptName, chassisDCNumber, checkPointName]);

  const handleCameraInitialization = useCallback(async () => {
    const imageFolderName = deptName + "_" + checkPointName;
    setFolderName(imageFolderName);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraPermission(true);
      stream.getTracks().forEach((track) => track.stop());

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter(
        (device) => device.kind === "videoinput"
      );
      setHasMultipleCameras(videoInputs.length > 1);

      try {
        await navigator.mediaDevices.getUserMedia({
          video: {
            width: { min: 1920, ideal: 3840, max: 3840 },
            height: { min: 1080, ideal: 2160, max: 2160 },
            facingMode: FACING_MODE_ENVIRONMENT,
          },
        });
        setFacingMode(FACING_MODE_ENVIRONMENT);
      } catch (backErr) {
        setFacingMode(FACING_MODE_USER);
        setError("Back camera unavailable, switched to front camera.");
      }

      setOpen(true);
      setError(null);

      createFolders();
    } catch (err) {
      setCameraPermission(false);
      setOpen(true);
      setError(
        "Camera access is required. Please enable it in your tablet settings."
      );
    }
  }, [deptName, checkPointName, createFolders]);

  const checkAuthentication = useCallback(async () => {
    try {
      setLoading(true);
      const response = await googleDriveAxios.get("/auth/login");
      const isAuth = response.data.message === "Already authenticated";
      setIsAuthenticated(isAuth);
      setLoading(false);
      return isAuth;
    } catch (error) {
      console.error("Authentication check failed:", error);
      setIsAuthenticated(false);
      setLoading(false);
      return false;
    }
  }, []);

  const handleGoogleLogin = useCallback(async () => {
    try {
      setLoading(true);
      const response = await googleDriveAxios.get("/auth/login");

      if (response.data.authUrl) {
        localStorage.setItem(
          "cameraReturnPath",
          window.location.pathname + window.location.search
        );

        window.open(response.data.authUrl, "_blank");

        toast.info("Please complete Google login in the new tab");
      } else if (response.data.message === "Already authenticated") {
        setIsAuthenticated(true);
        setShowLoginPrompt(false);
        handleCameraInitialization();
      }
      setLoading(false);
    } catch (error) {
      console.error("Login failed:", error);
      toast.error("Failed to login with Google");
      setLoading(false);
    }
  }, [handleCameraInitialization]);

  useEffect(() => {
    const returnPath = localStorage.getItem("cameraReturnPath");
    if (returnPath) {
      localStorage.removeItem("cameraReturnPath");
      checkAuthentication().then((isAuth) => {
        if (isAuth) {
          handleCameraInitialization();
        }
      });
    }
  }, [checkAuthentication, handleCameraInitialization]);

  const handleSwitchCamera = () => {
    setFacingMode((prevMode) =>
      prevMode === FACING_MODE_USER ? FACING_MODE_ENVIRONMENT : FACING_MODE_USER
    );
    setError(null);
  };

  const handleOpen = async () => {
    const isAuth = await checkAuthentication();

    if (isAuth) {
      await handleCameraInitialization();
    } else {
      setShowLoginPrompt(true);
      setOpen(true);
    }
  };

  const handleClose = () => {
    checkCheckpointRecord();

    if (webcamRef.current?.stream) {
      const tracks = webcamRef.current.stream.getTracks();
      tracks.forEach((track) => track.stop());
    }

    setOpen(false);
    setError(null);
    setCameraPermission(null);
    setShowLoginPrompt(false);
  };

  const handleCapture = () => {
    if (webcamRef.current?.video && canvasRef.current) {
      const video = webcamRef.current.video;
      const canvas = canvasRef.current;

      const context = canvas.getContext("2d");
      if (context) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        handleUpload();
      } else {
        setError("Failed to get canvas context.");
      }
    } else {
      setError("Camera or canvas not available.");
    }
  };

  const uploadCanvasImage = async (
    canvas: any,
    chassisNo: string,
    folderName: string
  ) => {
    canvas.toBlob(async (blob: any) => {
      if (!blob) return;

      const now = new Date();
      const timestamp = now
        .toISOString()
        .replace("T", "_")
        .replace(/:/g, "-")
        .replace(/\..+/, "");

      const fileName = `${timestamp}.png`;

      const file = new File([blob], fileName, {
        type: "image/png",
      });

      const formData = new FormData();
      formData.append("file", file);

      const queryParams = new URLSearchParams({
        chassis_no: chassisNo,
        folder_name: folderName,
      }).toString();

      toast
        .promise(
          googleDriveAxios.post(`/upload-image?${queryParams}`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }),
          {
            pending: "Uploading your image...",
            success: "Image uploaded successfully!",
            error: "Upload failed, please try again.",
          }
        )
        .then(async () => {
          await updateCheckpointImageRecord();
          checkCheckpointRecord();
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            toast.error("Authentication required. Please login again.");
            setShowLoginPrompt(true);
          }
        });
    }, "image/png");

    try {
      await updateCheckpointImageRecord();
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        setIsAuthenticated(false);
        setShowLoginPrompt(true);
      }
    }
  };

  const updateCheckpointImageRecord = async () => {
    const query = new URLSearchParams({
      chassis_no: chassisDCNumber,
      dept_name: deptName,
      checklist_point: checkPointName,
    }).toString();

    const response = await googleDriveAxios.post(
      `/checkpoint-image-record?${query}`
    );
    console.log(response.data);
  };

  const handleUpload = () => {
    if (canvasRef.current) {
      uploadCanvasImage(canvasRef.current, chassisDCNumber, folderName);
    } else {
      console.error("❌ Canvas not found");
    }
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{ color: "#6A1B9A", fontSize: "48px" }}
        aria-label="Open camera to scan chassis number"
      >
        <PhotoCameraIcon fontSize="small" />
      </IconButton>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="scan-dialog-title"
      >
        <DialogTitle
          id="scan-dialog-title"
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            backgroundColor: "#6A1B9A",
            color: "white",
          }}
        >
          {showLoginPrompt ? "Google Login Required" : "Camera"}
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "20px",
            backgroundColor: "#f5f5f5",
          }}
        >
          {loading ? (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </Box>
          ) : showLoginPrompt ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Google Drive access is required to upload images
              </Typography>
              <Button
                variant="contained"
                onClick={handleGoogleLogin}
                startIcon={<LoginIcon />}
                sx={{
                  backgroundColor: "#6A1B9A",
                  "&:hover": { backgroundColor: "#4a1266" },
                }}
              >
                Login with Google
              </Button>
            </Box>
          ) : cameraPermission === true ? (
            <Box
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: "640px",
                border: "2px solid #6A1B9A",
                borderRadius: "12px",
                overflow: "hidden",
              }}
            >
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={videoConstraints}
                style={{
                  width: "100%",
                  height: "auto",
                }}
                onUserMediaError={(err) => {
                  setError("Failed to initialize camera, using front camera.");
                  setFacingMode(FACING_MODE_USER);
                }}
              />

              <canvas
                ref={canvasRef}
                style={{
                  width: "100%",
                  height: "auto",
                  borderRadius: "12px",
                  zIndex: 5,
                  display: "none",
                }}
              />
            </Box>
          ) : (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              {error || "Checking camera permissions..."}
            </Typography>
          )}

          {!showLoginPrompt && cameraPermission === true && (
            <Box sx={{ display: "flex", gap: 3, mt: 2 }}>
              <IconButton
                onClick={handleSwitchCamera}
                disabled={!hasMultipleCameras || cameraPermission !== true}
                sx={{
                  color: "#6A1B9A",
                  backgroundColor: "rgba(106, 27, 154, 0.1)",
                  "&:hover": { backgroundColor: "rgba(106, 27, 154, 0.2)" },
                  padding: "8px",
                }}
                aria-label="Switch camera"
              >
                <FlipCameraIosIcon />
              </IconButton>
              <IconButton
                onClick={handleCapture}
                sx={{
                  color: "#6A1B9A",
                  backgroundColor: "rgba(106, 27, 154, 0.1)",
                  "&:hover": { backgroundColor: "rgba(106, 27, 154, 0.2)" },
                  padding: "8px",
                }}
                aria-label="Capture photo"
              >
                <CircleIcon />
              </IconButton>
              <IconButton
                onClick={handleClose}
                sx={{
                  color: "#6A1B9A",
                  backgroundColor: "rgba(106, 27, 154, 0.1)",
                  "&:hover": { backgroundColor: "rgba(106, 27, 154, 0.2)" },
                  padding: "8px",
                }}
                aria-label="Close scanner"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CameraCapture;
